import { useEffect, useState, useMemo } from "react";
import type { CSSProperties } from "react";
import { api } from "../api";
import { usePermissions } from "../hooks/usePermissions";
import { useTranslation } from "../i18n";
import { RAD, FS } from "../lib/tokens";
import Table from "./Table";

// Phase M+2b Chunk B+C — Permissions report panel for ReportsView.
// Two views: by collaborator (default) and by case.
// Data source: GET /api/reports/permissions (admin or RP-of-any).

export default function PermissionsReportPanel() {
  const { t } = useTranslation();
  const { swapping } = usePermissions();
  const [view, setView] = useState("byCollab");
  const [data, setData] = useState<{ caseGrants: any[]; ccGrants: any[] }>({ caseGrants: [], ccGrants: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (swapping) return;
    let cancelled = false;
    setLoading(true);
    (
      api.get("/reports/permissions") as Promise<{
        caseGrants?: unknown[];
        ccGrants?: unknown[];
      } | null>
    )
      .then((rows) => {
        if (cancelled) return;
        setData({
          caseGrants: Array.isArray(rows?.caseGrants) ? rows.caseGrants : [],
          ccGrants: Array.isArray(rows?.ccGrants) ? rows.ccGrants : [],
        });
        setErr(null);
      })
      .catch((e) => {
        if (!cancelled)
          setErr(
            e?.message ||
              t("common.failed_to_load", { fallback: "Failed to load" }),
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [swapping]);

  const allGrants = useMemo(() => {
    const c = data.caseGrants.map((g) => ({
      ...g,
      _kind: "case",
      _scopeRef: g.caseRef,
      _scopeTitle: g.caseTitle,
      _scopeId: g.caseId,
    }));
    const cc = data.ccGrants.map((g) => ({
      ...g,
      _kind: "cc",
      _scopeRef: g.ccRef,
      _scopeTitle: g.ccTitle,
      _scopeId: g.ccId,
    }));
    return [...c, ...cc];
  }, [data]);

  if (loading)
    return (
      <div style={{ padding: 16 }}>
        <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
          {t("common.loading", { fallback: "Loading…" })}
        </p>
      </div>
    );
  if (err)
    return (
      <div style={{ padding: 16 }}>
        <p style={{ fontSize: FS.s11, color: "var(--rd)" }}>{err}</p>
      </div>
    );

  const empty = allGrants.length === 0;

  const tabBtn = (key: string, label: any) => (
    <button
      type="button"
      onClick={() => setView(key)}
      style={{
        fontSize: FS.s10_5,
        padding: "4px 12px",
        borderRadius: RAD.r14,
        background: view === key ? "var(--as)" : "transparent",
        color: view === key ? "var(--ac)" : "var(--ts)",
        border: view === key ? "1px solid var(--ag)" : "1px solid var(--br)",
        cursor: "pointer",
        fontWeight: view === key ? 600 : 500,
        fontFamily: "inherit",
      }}
    >
      {label}
    </button>
  );

  const stateBadge = (allowed: any) => (
    <span
      style={{
        fontSize: FS.s9_5,
        fontWeight: 600,
        padding: "1px 8px",
        borderRadius: RAD.r10,
        background: allowed ? "rgba(34,197,94,.18)" : "rgba(245,158,11,.18)",
        color: allowed ? "var(--gn)" : "var(--am)",
        border: allowed
          ? "1px solid rgba(34,197,94,.35)"
          : "1px solid rgba(245,158,11,.35)",
      }}
    >
      {allowed
        ? t("permissions.granted", { fallback: "Granted" })
        : t("permissions.restricted", { fallback: "Restricted" })}
    </span>
  );

  const tableStyle: CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: FS.s11,
    marginTop: 8,
  };
  const thStyle: CSSProperties = {
    padding: "6px 8px",
    textAlign: "left",
    fontWeight: 600,
    color: "var(--tm)",
    borderBottom: "1px solid var(--br)",
  };
  const tdStyle: CSSProperties = {
    padding: "6px 8px",
    borderBottom: "1px solid rgba(255,255,255,.04)",
  };

  function renderByCollaborator() {
    const sorted = [...allGrants].sort((a, b) => {
      const an = a.collaboratorDisplayName || "";
      const bn = b.collaboratorDisplayName || "";
      const r = an.localeCompare(bn);
      if (r !== 0) return r;
      return String(a._scopeRef || "").localeCompare(String(b._scopeRef || ""));
    });
    return (
      <div style={{ overflowX: "auto" }}>
        <Table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>
                {t("permissions.collaborator", { fallback: "Collaborator" })}
              </th>
              <th style={thStyle}>{t("common.role", { fallback: "Role" })}</th>
              <th style={thStyle}>
                {t("permissions.case_or_cc", { fallback: "Case / CC" })}
              </th>
              <th style={thStyle}>
                {t("permissions.permission", { fallback: "Permission" })}
              </th>
              <th style={thStyle}>
                {t("permissions.state", { fallback: "State" })}
              </th>
              <th style={thStyle}>
                {t("permissions.granted_by", { fallback: "Granted by" })}
              </th>
              <th style={thStyle}>
                {t("permissions.granted_at", { fallback: "Granted at" })}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((g) => (
              <tr key={`${g._kind}:${g.id}`}>
                <td style={tdStyle}>
                  {g.collaboratorDisplayName ||
                    g.collaboratorEmail ||
                    `#${g.collaboratorId}`}
                </td>
                <td style={tdStyle}>{g.collaboratorRole || ""}</td>
                <td style={tdStyle}>
                  {g._scopeRef
                    ? `${g._scopeRef} — ${g._scopeTitle || ""}`
                    : g._scopeTitle || `#${g._scopeId}`}{" "}
                  <span style={{ fontSize: FS.s9, color: "var(--tm)" }}>
                    (
                    {g._kind === "cc"
                      ? t("permissions.cc_short", { fallback: "CC" })
                      : t("permissions.case_short", { fallback: "Case" })}
                    )
                  </span>
                </td>
                <td style={tdStyle} title={g.sectionKey}>
                  {g.sectionLabel}
                </td>
                <td style={tdStyle}>{stateBadge(g.allowed)}</td>
                <td style={tdStyle}>
                  {g.grantedByEmail || `#${g.grantedById}`}
                </td>
                <td style={tdStyle}>
                  {g.createdAt
                    ? new Date(g.createdAt).toLocaleDateString("fr-BE")
                    : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  function renderByCase() {
    const caseGrants = [...data.caseGrants].sort((a, b) =>
      String(a.caseRef || "").localeCompare(String(b.caseRef || "")),
    );
    const ccGrants = [...data.ccGrants].sort((a, b) =>
      String(a.ccRef || "").localeCompare(String(b.ccRef || "")),
    );
    const renderRows = (rows: any[], kind: string) =>
      rows.map((g) => (
        <tr key={`${kind}:${g.id}`}>
          <td style={tdStyle}>
            {g[kind === "cc" ? "ccRef" : "caseRef"] ||
              `#${g[kind === "cc" ? "ccId" : "caseId"]}`}
          </td>
          <td style={tdStyle}>
            {g[kind === "cc" ? "ccTitle" : "caseTitle"] || ""}
          </td>
          <td style={tdStyle}>
            {g.collaboratorDisplayName ||
              g.collaboratorEmail ||
              `#${g.collaboratorId}`}
          </td>
          <td style={tdStyle}>{g.collaboratorRole || ""}</td>
          <td style={tdStyle} title={g.sectionKey}>
            {g.sectionLabel}
          </td>
          <td style={tdStyle}>{stateBadge(g.allowed)}</td>
          <td style={tdStyle}>{g.grantedByEmail || `#${g.grantedById}`}</td>
          <td style={tdStyle}>
            {g.createdAt
              ? new Date(g.createdAt).toLocaleDateString("fr-BE")
              : ""}
          </td>
        </tr>
      ));
    return (
      <div style={{ overflowX: "auto" }}>
        {caseGrants.length > 0 && (
          <>
            <h4 style={{ margin: "12px 0 4px", fontSize: FS.s12 }}>
              {t("permissions.case_grants", { fallback: "Case grants" })}
            </h4>
            <Table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>
                    {t("permissions.case_ref", { fallback: "Case ref" })}
                  </th>
                  <th style={thStyle}>
                    {t("common.title", { fallback: "Title" })}
                  </th>
                  <th style={thStyle}>
                    {t("permissions.collaborator", {
                      fallback: "Collaborator",
                    })}
                  </th>
                  <th style={thStyle}>
                    {t("common.role", { fallback: "Role" })}
                  </th>
                  <th style={thStyle}>
                    {t("permissions.permission", { fallback: "Permission" })}
                  </th>
                  <th style={thStyle}>
                    {t("permissions.state", { fallback: "State" })}
                  </th>
                  <th style={thStyle}>
                    {t("permissions.granted_by", { fallback: "Granted by" })}
                  </th>
                  <th style={thStyle}>
                    {t("permissions.granted_at", { fallback: "Granted at" })}
                  </th>
                </tr>
              </thead>
              <tbody>{renderRows(caseGrants, "case")}</tbody>
            </Table>
          </>
        )}
        {ccGrants.length > 0 && (
          <>
            <h4 style={{ margin: "14px 0 4px", fontSize: FS.s12 }}>
              {t("permissions.cc_grants", {
                fallback: "Collective case grants",
              })}
            </h4>
            <Table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>
                    {t("permissions.cc_ref", { fallback: "CC ref" })}
                  </th>
                  <th style={thStyle}>
                    {t("common.title", { fallback: "Title" })}
                  </th>
                  <th style={thStyle}>
                    {t("permissions.collaborator", {
                      fallback: "Collaborator",
                    })}
                  </th>
                  <th style={thStyle}>
                    {t("common.role", { fallback: "Role" })}
                  </th>
                  <th style={thStyle}>
                    {t("permissions.permission", { fallback: "Permission" })}
                  </th>
                  <th style={thStyle}>
                    {t("permissions.state", { fallback: "State" })}
                  </th>
                  <th style={thStyle}>
                    {t("permissions.granted_by", { fallback: "Granted by" })}
                  </th>
                  <th style={thStyle}>
                    {t("permissions.granted_at", { fallback: "Granted at" })}
                  </th>
                </tr>
              </thead>
              <tbody>{renderRows(ccGrants, "cc")}</tbody>
            </Table>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: "8px 4px" }}>
      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 10,
          alignItems: "center",
        }}
      >
        {tabBtn(
          "byCollab",
          t("permissions.by_collaborator", { fallback: "By collaborator" }),
        )}
        {tabBtn("byCase", t("permissions.by_case", { fallback: "By case" }))}
      </div>
      {empty ? (
        <div style={{ padding: 16 }}>
          <p style={{ fontSize: FS.s11_5, color: "var(--tp)", marginBottom: 4 }}>
            {t("permissions.no_grants", {
              fallback: "No per-case grants exist.",
            })}
          </p>
          <p style={{ fontSize: FS.s10_5, color: "var(--tm)" }}>
            {t("permissions.no_grants_hint", {
              fallback:
                "RPs can grant teammates access from the 'Team access' panel inside any case they're responsible for.",
            })}
          </p>
        </div>
      ) : view === "byCollab" ? (
        renderByCollaborator()
      ) : (
        renderByCase()
      )}
    </div>
  );
}
